import { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { Auth } from "aws-amplify";
import styles from "./ForgotPassword.module.scss";
import ATextField from "../../../../components/UI/Inputs/MUI/ATextField/ATextField";
import Spinner from "../../../../components/UI/Spinner";
import HelpSection from "../HelpSection/HelpSection";
import { useTranslation } from 'react-i18next';

/**
 * Componente de recuperação de senha
 * @param {string} email - Email pré-preenchido (opcional)
 * @param {function} onBack - Função chamada quando o usuário clica em voltar
 * @param {function} onCodeSent - Função chamada quando o código é enviado com sucesso
 */
const ForgotPassword = ({ email, onBack, onCodeSent }) => {
  // Estado para armazenar os dados do formulário
  const [formData, setFormData] = useState({
    email: email || "",
    errorMsg: null,
    successMsg: null,
    isLoading: false
  });

  // Referência para o campo de email para foco automático
  const emailInputRef = useRef(null);

  // Tradução
  const { t } = useTranslation();

  // Efeito para focar no campo de email quando o componente é montado
  useEffect(() => {
    if (emailInputRef.current) {
      emailInputRef.current.focus();
    }
  }, []);

  // useEffect para observar a mudança na mensagem de sucesso
  useEffect(() => {
    if (formData.successMsg) {
      const timer = setTimeout(() => {
        // Passa para o próximo passo (redefinição de senha)
        if (onCodeSent) {
          onCodeSent(formData.email);
        }
      }, 1500);

      // Cleanup para evitar memory leaks se o componente for desmontado
      return () => clearTimeout(timer);
    }
  }, [formData.successMsg, formData.email, onCodeSent]);

  /**
   * Atualiza o estado do formulário com novos valores
   * @param {object} updates - Objeto com os campos a serem atualizados
   */
  const updateFormData = (updates) => {
    setFormData(prev => ({
      ...prev,
      ...updates
    }));
  };

  /**
   * Função para enviar o código de recuperação de senha
   * @param {Event} e - Evento do formulário
   */
  const handleSendResetCode = async (e) => {
    e.preventDefault();
    updateFormData({ isLoading: true, errorMsg: null, successMsg: null });

    try {
      // Chama a API do Amplify para solicitar o código de recuperação
      await Auth.forgotPassword(formData.email);
      updateFormData({
        isLoading: false,
        successMsg: "Código enviado com sucesso! Verifique seu e-mail."
      });

    } catch (error) {
      console.error("Erro ao enviar código:", error);
      let errorMessage = "Houve um erro ao processar sua solicitação.";

      // Trata erros específicos com mensagens personalizadas
      if (error.code === "UserNotFoundException") {
        errorMessage = "E-mail não encontrado. Verifique se digitou corretamente.";
      } else if (error.code === "LimitExceededException") {
        errorMessage = "Muitas tentativas. Tente novamente mais tarde.";
      }

      updateFormData({
        isLoading: false,
        errorMsg: errorMessage
      });
    }
  };

  return (
    <div>
      <h2>{t("login_page.forgot_password")}</h2>
      {formData.successMsg && (
        <div className={styles.success_message}>
          <span>✓</span> {formData.successMsg}
        </div>
      )}
      {formData.errorMsg && (
        <div className={styles.error_message}>
          <span>⚠</span> {formData.errorMsg}
        </div>
      )}
      <p>
        {t("login_page.account_registered")}
      </p>

      <form onSubmit={handleSendResetCode}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "24px" }}>
          <ATextField
            inputRef={emailInputRef}
            label="Digite seu e-mail"
            variant="outlined"
            required
            type="email"
            value={formData.email}
            error={!!formData.errorMsg}
            onChange={(e) => updateFormData({ email: e.target.value, errorMsg: null })}
            sx={{ backgroundColor: "#F2F2F2" }}
            disabled={formData.isLoading}
          />

          <button
            type="submit"
            className={styles.button_login}
            disabled={formData.isLoading || !formData.email.trim()}
          >
            {formData.isLoading ? <Spinner size="small" /> : "CONFIRMAR"}
          </button>

          <button
            type="button"
            className={styles.back}
            onClick={onBack}
            disabled={formData.isLoading}
          >
            {t("login_page.back_login")}
          </button>
        </Box>

        <HelpSection />
      </form>
    </div>
  );
};

export default ForgotPassword;